'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import classNames from 'classnames';
import { type FC, useEffect, useRef } from 'react';
import { buildUrl } from '../../helpers/links';
import { type ElementMappers, RichText } from '../../helpers/RichText';
import type { EditorialSlideType, SliderSectionType } from '../../types/storyblok';
import { AImage } from '../AImage';
import styles from './editorial-slide.module.scss';

const elementMappers: Partial<ElementMappers> = {
  p: ({ children }) => <Text size="l">{children}</Text>,
};

const scrollTargetIntoView = (e: { currentTarget: HTMLLIElement }) => {
  e.currentTarget.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
  });
};

export const EditorialSlide: FC<
  EditorialSlideType & {
    background: SliderSectionType['background'];
    id: string;
  }
> = ({ background, buttonLabel, buttonLink, headline, id, image, text }) => {
  const href = buttonLink ? buildUrl(buttonLink) : undefined;
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const slide = ref.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const intersectionRatio = entries.find((entry) => entry.target === ref.current)?.intersectionRatio ?? 1;

        slide.style.setProperty('--intersection-ratio', String(intersectionRatio > 0.9 ? 1 : intersectionRatio));
      },
      {
        threshold: Array.from({ length: 11 }, (_, i) => i / 10),
        root: slide.parentElement,
      },
    );

    observer.observe(slide);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: workaround
    // biome-ignore lint/a11y/useKeyWithClickEvents: workaround
    <li
      className={classNames(styles.slide, styles[`bg-${background}`])}
      id={id}
      onClick={scrollTargetIntoView}
      onFocus={scrollTargetIntoView}
      ref={ref}
    >
      <span className={styles.text}>
        {Boolean(headline) && <Headline renderAs="h4">{headline}</Headline>}
        <RichText elementMappers={elementMappers} richText={text} />
      </span>
      {href && (
        <span className={styles.link}>
          <BoxCTA as="a" href={href} variant="neutral-filled">
            {buttonLabel}
          </BoxCTA>
        </span>
      )}

      {image && (
        <span className={styles.image}>
          <AImage imageMobile={image} altText={image.alt ?? ''} className={styles.image} />
        </span>
      )}
    </li>
  );
};
